// src/components/NavSearch/NavSearch.jsx
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./NavSearch.css";
import MukemelLogo from "./../../assets/headernewlogo2.png";
import LatesProduct from "../LatestProduct/LatesProduct";
import { useTranslation } from "react-i18next";
import categoryData from "../../api/categories";
import { formatPage } from "../../utils/formatPage";
import { useCurrency } from "./../../components/PriceFormat/PriceFormat";

function isPerfumeProduct(p) {
  const norm = (x) =>
    String(x || "")
      .normalize("NFKD")
      .toLowerCase()
      .trim();
  const c = p?.category;
  if (Array.isArray(c)) {
    return c.some((cat) => {
      const n = norm(cat);
      return n === "perfume" || n === "parfum" || n === "parfüm";
    });
  }
  if (typeof c === "string") {
    const n = norm(c);
    return (
      n.includes("perfume") || n.includes("parfum") || n.includes("parfüm")
    );
  }
  return false;
}

// manual_prices'tan fiyat seç (yalnızca parfüm için)
function pickFromManual(mp, currency) {
  if (!mp) return null;
  let key = String(currency || "TRY").toUpperCase();
  if (key === "TL") key = "TRY";
  const v = mp?.[key];
  if (v == null || Number.isNaN(Number(v))) return null;
  return { amount: Number(v), curr: key };
}

/* --- SADECE displayPrices ile fiyat seçimi --- */
function pickFromDP(dp, currency) {
  if (!dp) return null;
  const up = String(currency || "TRY").toUpperCase();
  const scope = up === "TRY" || up === "TL" ? "tr" : "intl";

  // 1) İstenen para birimi
  let v = dp?.[scope]?.[up];
  if (v != null) return { amount: Number(v), curr: up };

  // 2) Aynı blokta USD fallback
  v = dp?.[scope]?.USD;
  if (v != null) return { amount: Number(v), curr: "USD" };

  // 3) Global fallback: intl.USD
  v = dp?.intl?.USD;
  if (v != null) return { amount: Number(v), curr: "USD" };

  // 4) Son çare: tr.TRY (en azından bir rakam göstermek için)
  v = dp?.tr?.TRY;
  if (v != null) return { amount: Number(v), curr: "TRY" };

  return null;
}

function formatMoney(amount, curr) {
  const up = String(curr).toUpperCase();
  const isTRY = up === "TRY" || up === "TL";
  const opts = isTRY
    ? amount < 1000
      ? { minimumFractionDigits: 1, maximumFractionDigits: 1 } // 4,5
      : { minimumFractionDigits: 0, maximumFractionDigits: 0 } // 12.500
    : { minimumFractionDigits: 0, maximumFractionDigits: 0 }; // 124 USD / 345 EUR / 567 GBP
  return `${Number(amount).toLocaleString("tr-TR", opts)} ${up}`;
}
/* ---------------------------------------------- */

const NavSearch = () => {
  const { t } = useTranslation();
  const { currency } = useCurrency();

  const [searchText, setSearchText] = useState("");
  const [index, setIndex] = useState(0);
  const [products, setProducts] = useState([]);

  // 🔹 Fiyat indeksi: id -> displayPrices
  const [priceIndex, setPriceIndex] = useState({}); // { [id]: displayPrices }
  const [pricesReady, setPricesReady] = useState(false);

  // Modal ilk kullanıma geldiğinde tüm ürünleri çek ve fiyat indeksini oluştur
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/products`
        );
        if (dead) return;
        const map = {};
        (Array.isArray(data) ? data : []).forEach((p) => {
          if (!p) return;
          map[p.id] = {
            dp: p.displayPrices || p.displayprices || null,
            mp: p.manual_prices || p.manualPrices || null,
          };
        });
        setPriceIndex(map);
      } catch (e) {
        // sessiz geç; indeks boş kalırsa yine de çalışır, sadece "-" görürüz
      } finally {
        if (!dead) setPricesReady(true);
      }
    })();
    return () => {
      dead = true;
    };
  }, []);

  const placeholderTexts = [
    t("Perfume"),
    t("Prestige"),
    t("Space Stylers"),
    t("Monuments"),
    t("Essence Of Form"),
    t("Art In Form"),
    t("Flame Holders"),
    t("Wall Masterpieces"),
    t("Read Display"),
    t("Light Style"),
    t("A Token Of Love"),
    t("Warm Sips"),
    t("Garden Charm"),
    t("Desk Elegance"),
  ];

  useEffect(() => {
    const iv = setInterval(
      () => setIndex((p) => (p + 1) % placeholderTexts.length),
      3000
    );
    return () => clearInterval(iv);
  }, [placeholderTexts.length]);

  // Arama
  const handleSearch = useCallback(async () => {
    const q = searchText.trim();
    if (!q) return;
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/products-filter`,
        { search: q }
      );
      setProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("search error:", error);
      setProducts([]);
    }
  }, [searchText]);

  useEffect(() => {
    const tmr = setTimeout(() => {
      if (searchText.trim()) handleSearch();
    }, 150);
    return () => clearTimeout(tmr);
  }, [searchText, handleSearch]);

  // Input temizlenince sonuçları da temizle → sadece Latest kalsın
  useEffect(() => {
    if (searchText.trim() === "") setProducts([]);
  }, [searchText]);

  const getProductImage = (product) => {
    if (product?.color_images?.[0]?.images?.[0]) {
      return product.color_images[0].images[0];
    }
    if (product?.default_images?.[0]) return product.default_images[0];
    return "https://apimukemel.com/api/assets/BTC0001-1.webp";
  };

  const renderPrice = (product) => {
    // İndeksten olası yedekleri al (dp/mp birlikte ya da sadece dp olabilir)
    const idx = priceIndex?.[product?.id];
    const dpFromIdx = idx && (idx.dp || idx); // geriye uyum: eski map "dp nesnesi" idi
    const mpFromIdx = idx && idx.mp;

    // 1) Parfümse manual_prices öncelikli
    if (isPerfumeProduct(product)) {
      const pickedManual =
        pickFromManual(
          product?.manual_prices || product?.manualPrices,
          currency
        ) || pickFromManual(mpFromIdx, currency);
      if (pickedManual) {
        return formatMoney(pickedManual.amount, pickedManual.curr);
      }
    }

    // 2) Parfüm değilse veya manual yoksa: displayPrices
    const dp =
      product?.displayPrices || product?.displayprices || dpFromIdx || null;
    const picked = pickFromDP(dp, currency);
    if (!picked) return "-"; // legacy price kullanmıyoruz
    return formatMoney(picked.amount, picked.curr);
  };

  return (
    <>
      <div
        className="modal fade"
        id="searchModal"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-fullscreen">
          <div className="modal-content">
            <div className="modal-header d-flex justify-content-center align-items-center border-0">
              <h5 className="modal-title mt-3" id="exampleModalLabel">
                <a href="/">
                  <img src={MukemelLogo} alt="Mukemel" width={100} />
                </a>
              </h5>
              <button
                type="button"
                className="btn-close position-absolute end-0 me-3"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>

            <div className="modal-body pe-0 px-0">
              <div className="container">
                <div className="row justify-content-center">
                  <div className="col-12 col-md-6 col-sm-8 col-lg-8">
                    <form
                      className="d-flex mb-3 position-relative"
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleSearch();
                      }}
                    >
                      <input
                        className="form-control me-2 rounded-2 p-3 pe-5 animated-placeholder"
                        type="search"
                        placeholder={`${t("Search for")} ${
                          placeholderTexts[index]
                        }`}
                        aria-label="Search"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                      {searchText && (
                        <span
                          className="position-absolute end-0 top-50 translate-middle-y me-4 text-secondary fs-11"
                          style={{ cursor: "pointer" }}
                          onClick={() => setSearchText("")}
                        >
                          {t("Clear")}
                        </span>
                      )}
                    </form>

                    <div className="text-center mb-4">
                      <span className="fs-10 text-dark text-uppercase mx-3">
                        {t("Trending Searches")}
                      </span>
                      {categoryData.category.slice(0, 4).map((cat) => (
                        <a
                          key={cat.id}
                          href={`/category/${formatPage(cat.categoryname)}`}
                          className="text-dark text-decoration-none fw-14 mx-2"
                        >
                          {t(cat.translationKey)}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Arama kutusu DOLUYSA sonuçlar */}
              {searchText.trim() !== "" && (
                <div className="container-fluid">
                  <div className="row g-0">
                    {products.length > 0 ? (
                      products.map((product) => {
                        const inStock = String(product?.availability || "")
                          .toLowerCase()
                          .includes("in stock");
                        return (
                          <div
                            key={product.id}
                            className="col-lg-3 col-md-6 col-sm-6 col-12 mb-4 pe-0 px-0 mt-4"
                          >
                            <a
                              href={`/product/${product.id}`}
                              className="text-decoration-none"
                            >
                              <div className="cardbg">
                                <img
                                  src={getProductImage(product)}
                                  alt={product.name}
                                  className="d-block w-100"
                                />
                              </div>
                              <div className="px-4 bg-light pt-2 pb-2">
                                <h5 className="mb-0 text-dark">
                                  {product.name}
                                </h5>
                                {inStock ? (
                                  <h6 className="text-muted mt-1">
                                    {renderPrice(product)}
                                  </h6>
                                ) : (
                                  <h6 className="fw-bold mt-1 text-danger">
                                    Out of Stock!
                                  </h6>
                                )}
                              </div>
                            </a>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-5 text-muted w-100">
                        {pricesReady ? t("No products found") : t("Loading...")}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Arama kutusu BOŞSA sadece Latest Added Products */}
              {searchText.trim() === "" && (
                <div className="container-fluid mt-5 pe-0 px-0">
                  <h6 className="fw-normal mx-5">
                    {t("Latest Added Products")}
                  </h6>
                  <div className="row pe-0 px-0 g-0">
                    <LatesProduct />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NavSearch;
